import React, { useState, useEffect } from 'react';
import { Button, Input } from 'antd';
import { Phone, Plus, ChevronDown, ChevronUp, Play, Pause, Trash } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ShortUniqueId from 'short-unique-id';

import callTransferSample from '../../../assets/sounds/samples/call-transfer-workflow-sample.mp3';
import { useAudioPlayer } from './hooks/useAudioPlayer';

const { TextArea } = Input;

interface CallWorkflow {
	id: string;
	description: string;
	workflowForwardingNumber: string;
	workflowForwardingDID?: string;
	backupNumbers: string[];
	voiceResponse: string;
	whisperType: string;
	handoffMessageType: string;
	handoffMessage: string;
	callTransferee: string;
	displayedNumber: string;
}

interface OnboardingData {
	welcomeCompleted: boolean;
	businessName: string;
	phoneNumber: string;
	addresses: any[];
	businessHours: any;
	pricing: any[];
	faqs: any[];
	additionalInfo: string;
	callWorkflows: CallWorkflow[];
}

interface CallTransferWorkflowStageProps {
	onboardingData: OnboardingData;
	updateOnboardingData: (updates: Partial<OnboardingData>) => void;
}

const CallTransferWorkflowStage: React.FC<CallTransferWorkflowStageProps> = ({
	onboardingData,
	updateOnboardingData,
}) => {
	// Initialize with first workflow expanded if exists, otherwise null initially
	const [expandedWorkflow, setExpandedWorkflow] = useState<string | null>(() => {
		if (onboardingData.callWorkflows && onboardingData.callWorkflows.length > 0) {
			return onboardingData.callWorkflows[0].id;
		}
		return null;
	});

	const [showValidationErrors, setShowValidationErrors] = useState(false);

	// Auto-create first workflow if none exist
	useEffect(() => {
		if (!onboardingData.callWorkflows || onboardingData.callWorkflows.length === 0) {
			const firstWorkflow: CallWorkflow = {
				id: new ShortUniqueId({ length: 10 }).rnd(),
				description: '',
				workflowForwardingNumber: '',
				workflowForwardingDID: '',
				backupNumbers: [],
				voiceResponse: 'Please hold while I transfer your call.',
				whisperType: 'Cold Transfer (Without Summary)',
				handoffMessageType: 'Prompt',
				handoffMessage: '',
				callTransferee: 'No',
				displayedNumber: "Receptionist's Number",
			};

			updateOnboardingData({
				callWorkflows: [firstWorkflow],
			});

			// Expand the newly created workflow
			setExpandedWorkflow(firstWorkflow.id);
		}
	}, [onboardingData.callWorkflows, updateOnboardingData]);

	const { audioRef, isPlaying, currentTime, duration, isLoading, togglePlayPause, formatTime } =
		useAudioPlayer({ audioSrc: callTransferSample });

	const addWorkflow = () => {
		const newWorkflow: CallWorkflow = {
			id: new ShortUniqueId({ length: 10 }).rnd(),
			description: '',
			workflowForwardingNumber: '',
			workflowForwardingDID: '',
			backupNumbers: [],
			voiceResponse: 'Please hold while I transfer your call.',
			whisperType: 'Cold Transfer (Without Summary)',
			handoffMessageType: 'Prompt',
			handoffMessage: '',
			callTransferee: 'No',
			displayedNumber: "Receptionist's Number",
		};

		updateOnboardingData({
			callWorkflows: [...onboardingData.callWorkflows, newWorkflow],
		});

		// Expand the newly added workflow
		setExpandedWorkflow(newWorkflow.id);
	};

	const removeWorkflow = (id: string) => {
		updateOnboardingData({
			callWorkflows: onboardingData.callWorkflows?.filter((w) => w.id !== id),
		});
	};

	const updateWorkflow = (id: string, field: keyof CallWorkflow, value: any) => {
		updateOnboardingData({
			callWorkflows: onboardingData.callWorkflows?.map((w) =>
				w.id === id ? { ...w, [field]: value } : w,
			),
		});
	};

	// Backup number functions - commented out for now
	/*
	const addBackupNumber = (workflowId: string) => {
		const workflow = onboardingData.callWorkflows?.find((w) => w.id === workflowId);
		if (workflow) {
			updateWorkflow(workflowId, 'backupNumbers', [...workflow.backupNumbers, '']);
		}
	};

	const removeBackupNumber = (workflowId: string, index: number) => {
		const workflow = onboardingData.callWorkflows?.find((w) => w.id === workflowId);
		if (workflow) {
			const newBackupNumbers = workflow.backupNumbers.filter((_, i) => i !== index);
			updateWorkflow(workflowId, 'backupNumbers', newBackupNumbers);
		}
	};

	const updateBackupNumber = (workflowId: string, index: number, value: string) => {
		const workflow = onboardingData.callWorkflows?.find((w) => w.id === workflowId);
		if (workflow) {
			const newBackupNumbers = [...workflow.backupNumbers];
			newBackupNumbers[index] = value;
			updateWorkflow(workflowId, 'backupNumbers', newBackupNumbers);
		}
	};
	*/

	const toggleWorkflowExpanded = (workflowId: string) => {
		setExpandedWorkflow(expandedWorkflow === workflowId ? null : workflowId);
	};

	// Listen for validation attempts from parent
	useEffect(() => {
		const handleValidation = () => {
			setShowValidationErrors(true);
		};

		window.addEventListener('trigger-validation', handleValidation);
		return () => window.removeEventListener('trigger-validation', handleValidation);
	}, []);

	return (
		<div className="max-w-2xl mx-auto px-4">
			{/* Hidden audio element */}
			<audio ref={audioRef} src={callTransferSample} />

			{/* Friendly Header */}
			<div className="text-center mb-6">
				<h2 className="text-2xl font-bold text-blue-600 mb-3">Set Up Call Transfers 📞</h2>
				<p className="text-gray-600 text-sm leading-relaxed">
					Help your AI receptionist know when to transfer calls to the right person
				</p>
			</div>

			{/* Sample Call Transfer */}
			<div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-sm">
				<h3 className="text-sm font-medium text-gray-700 mb-2">
					How our AI handles a sample call transfer
				</h3>
				<div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-200">
					<button
						className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 flex items-center justify-center transition-all duration-200 shadow-sm"
						onClick={togglePlayPause}
						disabled={isLoading}
					>
						{isLoading ? (
							<div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
						) : isPlaying ? (
							<Pause className="w-4 h-4 text-white" />
						) : (
							<Play className="w-4 h-4 text-white ml-0.5" />
						)}
					</button>

					<div className="flex-1 relative">
						<div className="h-1.5 bg-blue-200/40 rounded-full overflow-hidden">
							<div
								className="h-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-100"
								style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
							/>
						</div>
					</div>

					<span className="text-xs text-blue-600/70 font-medium min-w-[30px]">
						{formatTime(currentTime)}
					</span>
				</div>
			</div>

			{/* Call Transfer Workflows Section */}
			<div className="space-y-4">
				{onboardingData.callWorkflows?.length > 0
					? onboardingData.callWorkflows?.map((workflow, index) => (
							<div
								key={workflow.id}
								className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl shadow-sm overflow-hidden"
							>
								<div
									className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/30 transition-all duration-200"
									onClick={() => toggleWorkflowExpanded(workflow.id)}
								>
									<div className="flex items-center gap-3">
										<div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
											<Phone className="w-4 h-4 text-white" />
										</div>
										<h3 className="font-semibold text-gray-800">
											{workflow.description || `Transfer Workflow ${index + 1}`}
										</h3>
									</div>
									<div className="flex items-center gap-2">
										{onboardingData.callWorkflows?.length > 1 && (
											<Button
												size="small"
												className="rounded-full bg-red-500 border-red-500 hover:bg-red-600 hover:border-red-600 text-white"
												onClick={(e) => {
													e.stopPropagation();
													removeWorkflow(workflow.id);
												}}
											>
												<Trash className="w-4 h-4" />
											</Button>
										)}
										{expandedWorkflow === workflow.id ? (
											<ChevronUp className="w-5 h-5 text-gray-600" />
										) : (
											<ChevronDown className="w-5 h-5 text-gray-600" />
										)}
									</div>
								</div>

								{expandedWorkflow === workflow.id && (
									<div className="px-4 pb-4 space-y-4">
										{/* Scenario */}
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Scenario Description <span className="text-red-500">*</span>
											</label>
											<div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 hover:bg-white transition-all duration-200">
												<TextArea
													value={workflow.description}
													onChange={(e) =>
														updateWorkflow(workflow.id, 'description', e.target.value)
													}
													placeholder="e.g., 'When customer asks for technical support' or 'When customer wants to speak to sales'"
													rows={3}
													className="border-0 shadow-none bg-transparent resize-none"
													status={
														showValidationErrors && !workflow.description.trim() ? 'error' : ''
													}
												/>
											</div>
											{showValidationErrors && !workflow.description.trim() && (
												<div className="text-red-500 text-xs mt-1">
													Please describe when this transfer should happen
												</div>
											)}
										</div>

										{/* Primary Transfer Number */}
										<div style={{ position: 'relative', zIndex: 50 }}>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Phone Number
											</label>
											<div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-200">
												<PhoneInput
													placeholder="Enter Phone Number"
													country="us"
													value={workflow.workflowForwardingNumber}
													onChange={(value: string) =>
														updateWorkflow(workflow.id, 'workflowForwardingNumber', value)
													}
													preferredCountries={['us', 'ca', 'gb', 'pk']}
													autoFormat={true}
													containerStyle={{
														width: '100%',
													}}
													inputStyle={{
														width: '100%',
														height: '40px',
														fontSize: '14px',
														border: 'none',
														background: 'transparent',
														fontFamily: 'DM Sans, sans-serif',
													}}
													buttonStyle={{
														border: 'none',
														background: 'transparent',
														borderRadius: '0.75rem 0 0 0.75rem',
													}}
													dropdownStyle={{
														zIndex: 9999,
														maxHeight: '200px',
														fontFamily: 'DM Sans, sans-serif',
													}}
												/>
											</div>
											{showValidationErrors && !workflow.workflowForwardingNumber.trim() && (
												<div className="text-red-500 text-xs mt-2">
													Please enter a phone number for transfers
												</div>
											)}
										</div>

										{/* Voice Response */}
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Voice Response
											</label>
											<div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 hover:bg-white transition-all duration-200">
												<TextArea
													value={workflow.voiceResponse}
													onChange={(e) =>
														updateWorkflow(workflow.id, 'voiceResponse', e.target.value)
													}
													placeholder="Please hold while I transfer your call."
													rows={3}
													className="border-0 shadow-none bg-transparent resize-none"
												/>
											</div>
										</div>

										{/* Advanced Settings - Hidden for now */}
										{/* 
										<div className="bg-white/90 backdrop-blur-sm rounded-xl hover:bg-white transition-all duration-200">
											<Collapse
												ghost
												className="advanced-settings-collapse"
												expandIcon={({ isActive }) =>
													isActive ? (
														<ChevronUp className="w-4 h-4 text-gray-600" />
													) : (
														<ChevronDown className="w-4 h-4 text-gray-600" />
													)
												}
												items={[
													{
														key: '1',
														label: (
															<div className="flex items-center gap-3 p-2">
																<div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
																	<Settings className="w-3 h-3 text-purple-600" />
																</div>
																<span className="font-medium text-gray-700">Advanced Settings</span>
															</div>
														),
														children: (
															<div className="px-4 pb-4 space-y-4">
																<div>
																	<label className="block text-sm font-medium text-gray-700 mb-2">
																		Backup Numbers (if primary is busy)
																	</label>
																	<div className="space-y-2">
																		{workflow.backupNumbers.map((number, index) => (
																			<div key={index} className="flex gap-2">
																				<div className="flex-1 bg-white/90 backdrop-blur-sm rounded-xl hover:bg-white transition-all duration-200">
																					<Input
																						value={number}
																						onChange={(e) =>
																							updateBackupNumber(workflow.id, index, e.target.value)
																						}
																						placeholder="+1 (555) 123-4568"
																						className="border-0 shadow-none bg-transparent"
																					/>
																				</div>
																				<Button
																					onClick={() => removeBackupNumber(workflow.id, index)}
																					className="rounded-xl bg-red-500 border-red-500 hover:bg-red-600 hover:border-red-600 text-white"
																				>
																					<Trash className="w-4 h-4" />
																				</Button>
																			</div>
																		))}
																		<Button
																			onClick={() => addBackupNumber(workflow.id)}
																			className="w-full rounded-xl border-2 border-dashed border-purple-200 text-purple-600 hover:border-purple-300 hover:bg-purple-50"
																		>
																			<Plus className="w-4 h-4 mr-1" />
																			Add Backup Number
																		</Button>
																	</div>
																</div>

																<div>
																	<label className="block text-sm font-medium text-gray-700 mb-2">
																		Transfer Type
																	</label>
																	<Select
																		value={workflow.whisperType}
																		onChange={(value) =>
																			updateWorkflow(workflow.id, 'whisperType', value)
																		}
																		className="w-full"
																	>
																		<Option value="Cold Transfer (Without Summary)">
																			Warm Transfer (AI introduces the caller)
																		</Option>
																		<Option value="Hot Transfer (With Summary)">
																			Cold Transfer (Direct transfer)
																		</Option>
																	</Select>
																</div>

																<div>
																	<label className="block text-sm font-medium text-gray-700 mb-2">
																		Caller ID Display
																	</label>
																	<Select
																		value={workflow.displayedNumber}
																		onChange={(value) =>
																			updateWorkflow(workflow.id, 'displayedNumber', value)
																		}
																		className="w-full"
																	>
																		<Option value="Show Caller's Number">
																			Show Caller's Number
																		</Option>
																		<Option value="Receptionist's Number">
																			Show Receptionist's Number
																		</Option>
																	</Select>
																</div>

																{workflow.whisperType === 'Hot Transfer (With Summary)' && (
																	<div>
																		<label className="block text-sm font-medium text-gray-700 mb-2">
																			Handoff Message Type
																		</label>
																		<Radio.Group
																			value={workflow.handoffMessageType}
																			onChange={(e) =>
																				updateWorkflow(
																					workflow.id,
																					'handoffMessageType',
																					e.target.value,
																				)
																			}
																			className="w-full"
																		>
																			<Radio value="Prompt">
																				Prompt (Used for generating whisper message)
																			</Radio>
																			<Radio value="Static Word">
																				Static Word (Whisper message)
																			</Radio>
																		</Radio.Group>

																		<TextArea
																			value={workflow.handoffMessage}
																			onChange={(e) =>
																				updateWorkflow(
																					workflow.id,
																					'handoffMessage',
																					e.target.value,
																				)
																			}
																			placeholder="e.g. Summarize the user's problem and say goodbye to next agent."
																			rows={2}
																			className="mt-2 rounded-xl border-0 shadow-none bg-gray-50/50 resize-none"
																		/>
																	</div>
																)}
															</div>
														),
													},
												]}
											/>
										</div> 
										*/}
									</div>
								)}
							</div>
						))
					: null}

				<Button
					onClick={addWorkflow}
					className="w-full h-12 text-base rounded-xl border-2 border-dashed border-green-200 text-green-600 hover:border-green-300 hover:bg-green-50 transition-all duration-200"
				>
					<Plus className="w-5 h-5 mr-2" />
					Add Another Transfer Workflow
				</Button>
			</div>
		</div>
	);
};

export default CallTransferWorkflowStage;
